import { Select } from '@mui/material';
import { DocumentType, graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { ComponentProps, FC } from 'react';
import { useIntl } from 'react-intl';
import { CreatableSelect } from './CreatableSelect';
import { useSnackbar } from '../../store/snackbar';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';

const listMetricDataSourceQuery = graphql(`
	query connections_listMetricDataSet($sourceId: UUID!) {
		listMetricDataSet(filter: { source: { id: { eq: $sourceId } } }) {
			id
			name
		}
	}
`);

const createMetricDataSetMutation = graphql(`
	mutation connections_createMetricDataSet($sourceId: UUID!, $name: String!) {
		createMetricDataSet(data: { source: { connect: { id: $sourceId } }, name: $name }) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

type MuiSelectProps = ComponentProps<typeof Select<string>>;

type Props = Omit<MuiSelectProps, 'onChange'> &
	Readonly<{
		onChange?: (value: string) => void;
		sourceId: string | undefined;
	}>;

const MetricDataSetSelect: FC<Props> = ({ sourceId, disabled, placeholder, value, onChange, ...props }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { isLoading: areItemsLoading, data: itemsData } = useApiQuery(listMetricDataSourceQuery, {
		enabled: sourceId != null,
		variables: { sourceId: sourceId ?? '' },
	});

	const { mutate: createItem, isPending: isItemCreatePending } = useApiMutation(createMetricDataSetMutation);

	const items =
		itemsData?.listMetricDataSet.map(({ id, name }) => ({
			value: id,
			label: name,
		})) ?? [];

	const handleOnCreate = (name: string) => {
		createItem(
			{
				sourceId: sourceId ?? '',
				name,
			},
			{
				onSuccess: handleSuccessfulCreate,
			},
		);
	};

	const handleSuccessfulCreate = (data: DocumentType<typeof createMetricDataSetMutation>) => {
		if (data.createMetricDataSet.ok) {
			onChange?.(data.createMetricDataSet.node?.id ?? '');
		} else {
			console.error(data.createMetricDataSet.errorMessage);
			mutationResponseHandler({
				result: false,
				toastMessages: { error: intl.formatMessage({ id: 'metric-data-set.error.create' }) },
				openSnackbar,
			});
		}
	};

	return (
		<CreatableSelect
			{...props}
			onChange={onChange}
			disabled={disabled || sourceId == null}
			loading={areItemsLoading || isItemCreatePending}
			value={value ?? '' /* value cannot be undefined in order to keep the input controlled */}
			items={items}
			createItemTitle={intl.formatMessage({ id: 'form.metric.create-new-data-set' })}
			createDialogTitle={intl.formatMessage({ id: 'form.metric.new-data-set' })}
			emptyItemErrorMessage={intl.formatMessage({ id: 'form.create-data-set.error.empty-name' })}
			onCreate={handleOnCreate}
		/>
	);
};

export { MetricDataSetSelect };
